import type { ComponentType, ReactNode } from 'react';
import type { IconProps } from '@phosphor-icons/react';
import { cx } from '../cx';
import { Icon } from '../Icon';

export type FactSheetItem = {
  id: string;
  /** What the fact is ("Languages"). */
  label: ReactNode;
  /** The fact itself; links allowed ("Finnish, Swedish"). */
  value: ReactNode;
  icon?: ComponentType<IconProps>;
  /** A short qualifier under the value ("6 days left"). */
  note?: ReactNode;
  /** Takes the full row (a value with chips or a long list). */
  wide?: boolean;
};

export type FactSheetProps = {
  items: FactSheetItem[];
  /** One or two sentences that say what this is, before any figure ("at a glance"). */
  summary?: ReactNode;
  /** Small label above the summary ("At a glance"). */
  summaryLabel?: ReactNode;
  /** Leads the sheet above the facts: the one figure that decides most (pay on a job page). */
  header?: ReactNode;
  /** Sits beside `header` from md up (where and how on a job page), so the lead row uses the width. */
  headerAside?: ReactNode;
  /** A full-width block between the facts and the footer (compact expandable rows, such as the Plus insights on a job page). */
  insights?: ReactNode;
  /** A line under the facts: a prompt or a note that belongs with them. */
  footer?: ReactNode;
  /** Accessible name of the sheet (rendered visually hidden). */
  label?: ReactNode;
  /** Facts per row on wide screens (2 per row at every width below that). */
  columns?: 2 | 3 | 4;
  /** No shadow: inside another card (never nest two shadowed cards). */
  flat?: boolean;
  className?: string;
};

/**
 * The scan-first summary of a thing someone has to decide about (a job): one card, an optional
 * lead figure, then label / value pairs in an even grid, so the eye can run down the labels and
 * stop at the one that matters. Labels are always words (never icon-only); icons only help scanning.
 */
export function FactSheet({
  items,
  summary,
  summaryLabel,
  header,
  headerAside,
  insights,
  footer,
  label,
  columns = 2,
  flat = false,
  className,
}: FactSheetProps) {
  if (items.length === 0 && !header && !summary) return null;
  return (
    <section
      className={cx(
        'overflow-hidden rounded-jc-container border border-jc-card-border bg-jc-surface text-jc-ink',
        !flat && 'shadow-jc-sm',
        className,
      )}
    >
      {label ? <h2 className="sr-only">{label}</h2> : null}
      {summary ? (
        <div className="flex flex-col gap-1.5 border-b border-jc-border px-5 py-5 md:px-7 md:py-6">
          {summaryLabel ? <span className="type-eyebrow text-jc-ink-secondary">{summaryLabel}</span> : null}
          <p className="m-0 type-subheading text-jc-ink">{summary}</p>
        </div>
      ) : null}
      {header || headerAside ? (
        <div
          className={cx(
            'grid gap-6 border-b border-jc-border px-5 py-5 md:px-7 md:py-6',
            Boolean(header && headerAside) && 'md:grid-cols-2 md:gap-10',
          )}
        >
          {header ? <div className="min-w-0">{header}</div> : null}
          {headerAside ? <div className="min-w-0">{headerAside}</div> : null}
        </div>
      ) : null}
      {items.length > 0 ? (
        <dl
          className={cx(
            // Two facts per row even on phones (values are short), so the sheet stays low.
            'm-0 grid grid-cols-2 gap-x-6 gap-y-6 px-5 py-6 md:gap-x-10 md:px-7',
            columns === 4 ? 'sm:grid-cols-2 2xl:grid-cols-4' : columns === 3 ? 'sm:grid-cols-2 lg:grid-cols-3' : 'sm:grid-cols-2',
          )}
        >
          {items.map((item) => (
            <div key={item.id} className={cx('flex min-w-0 flex-col gap-1', item.wide && 'sm:col-span-full')}>
              <dt className="flex items-center gap-1.5 type-label text-jc-ink-secondary">
                {item.icon ? <Icon icon={item.icon} size="sm" /> : null}
                {item.label}
              </dt>
              <dd className="m-0 min-w-0 type-body break-words hyphens-auto text-jc-ink">{item.value}</dd>
              {item.note ? <dd className="m-0 type-caption text-jc-ink-secondary">{item.note}</dd> : null}
            </div>
          ))}
        </dl>
      ) : null}
      {insights ? <div className="border-t border-jc-border px-4 py-3 md:px-6">{insights}</div> : null}
      {footer ? (
        <div className="flex flex-col gap-2 border-t border-jc-border bg-jc-elevated/40 px-5 py-4 type-caption text-jc-ink-secondary md:px-7">
          {footer}
        </div>
      ) : null}
    </section>
  );
}
